import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

/**
 * MathRenderer - Renders mathematical expressions using KaTeX
 * Supports inline and block math rendering with error fallback
 */
export const MathRenderer = ({ math, block = false, className = '' }) => {
  const Component = block ? BlockMath : InlineMath;
  
  try {
    return (
      <span className={className}>
        <Component math={math} />
      </span>
    );
  } catch (error) {
    // Fallback to plain text if KaTeX fails
    return <span className={className}>{math}</span>;
  }
};

/**
 * MathText - Renders text that may contain LaTeX math expressions
 * Math expressions are wrapped in $ for inline or $$ for block
 * Example: "Given: $2x + 5 = 15$" renders "Given:" as text and the equation in LaTeX
 */
export const MathText = ({ children, className = '' }) => {
  if (typeof children !== 'string') {
    return <span className={className}>{children}</span>;
  }
  
  // Split text by math delimiters ($ for inline)
  const parts = [];
  let remaining = children;
  let key = 0;
  
  // Handle inline math with single $
  const regex = /\$([^$]+)\$/g;
  let match;
  let lastIndex = 0;
  
  while ((match = regex.exec(remaining)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(
        <span key={key++}>{remaining.slice(lastIndex, match.index)}</span>
      );
    }
    // Add the math
    parts.push(
      <MathRenderer key={key++} math={match[1]} />
    );
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < remaining.length) {
    parts.push(
      <span key={key++}>{remaining.slice(lastIndex)}</span>
    );
  }
  
  // If no math found, return plain text
  if (parts.length === 0) {
    return <span className={className}>{children}</span>;
  }
  
  return <span className={className}>{parts}</span>;
};

export default MathRenderer;
